/**
 * Список заказов: строка, итог, отборы, окна периода и «сводка по товарам».
 *
 * ЧИСЛА СЧИТАЕТ СЕРВЕР — итог, имя поставщика и «на сколько заказов платёж»
 * приезжают готовыми. Здесь только подписи: считать те же величины второй раз
 * значило бы завести два ответа на один вопрос.
 */

import { apiFetch } from "@/lib/api";
import type { SmartIds } from "@/lib/crmSmartButtons";
import { iso, plusDays } from "@/lib/dates";
import { num } from "@/lib/format";
import type { CargoTrack } from "@/lib/orders/cargo";
import type { GoodsCounts, UnitStatusColumn } from "@/lib/orders/columns";
import type { CargoRef, CommentMark, PayMismatch, StageMark, StripItem } from "@/lib/orders/common";
import { plural } from "@/lib/plans";

/** Окна периода: те же привычки чтения, что у «Оплат». Общие для списка и дашборда. */
export function orderWindows() {
  const сегодня = new Date();
  const t = iso(сегодня);
  const первое = (год: number, месяц: number) => iso(new Date(год, месяц, 1));
  return [
    { label: "30 дней", hint: "29 дней назад и сегодня", from: iso(plusDays(сегодня, -29)), to: t },
    { label: "90 дней", hint: "последние три месяца", from: iso(plusDays(сегодня, -89)), to: t },
    {
      label: "этот месяц",
      hint: "с первого числа по сегодня",
      from: первое(сегодня.getFullYear(), сегодня.getMonth()),
      to: t,
    },
    {
      label: "этот год",
      hint: "с первого января по сегодня",
      from: первое(сегодня.getFullYear(), 0),
      to: t,
    },
  ];
}

/** Окна «даты приёма» (разметка 20.10.26 п.6): «что приняли вчера» — первым. */
export function receiptWindows() {
  const сегодня = new Date();
  const t = iso(сегодня);
  const вчера = iso(plusDays(сегодня, -1));
  return [
    { label: "вчера", hint: "штуки, принятые вчера", from: вчера, to: вчера },
    { label: "сегодня", hint: "штуки, принятые сегодня", from: t, to: t },
    { label: "7 дней", hint: "шесть дней назад и сегодня", from: iso(plusDays(сегодня, -6)), to: t },
    { label: "30 дней", hint: "29 дней назад и сегодня", from: iso(plusDays(сегодня, -29)), to: t },
  ];
}

export type OrderRow = GoodsCounts & {
  order_id: number;
  platform_order: string | null;
  ordered_at: string | null;
  plan_arrive_at: string | null;
  supplier_id: number | null;
  supplier: string;
  supplier_full: string | null;
  status_id: number | null;
  status: string | null;
  cargo_status_id: number | null;
  cargo_stage: string | null;
  cargo_code: string | null;
  /** `null` — сказать нечего (прочерк), 0 — настоящий ноль. */
  goods: number | null;
  accepted: number | null;
  waiting: number | null;
  positions: number | null;
  sum_goods: number;
  sum_delivery: number;
  rate: number | null;
  /** Платёж показан ЦЕЛИКОМ, как в CRM. */
  paid: number;
  payments: number;
  /** На сколько заказов пришёлся самый широкий из платежей заказа. */
  payment_orders_max: number;
  qr_ids: number[];
  plan_dates: number;
  plan_qty: number;
  plan_date_ids: number[];
  plan_ids: number[];
  strip: StripItem[];
  cargo: CargoRef[];
  /** Треки по России: перевозчик, номер, город назначения, даты ПЭК. */
  ru: CargoTrack[];
  /** `null` — открытых комментариев нет. */
  comments: CommentMark | null;
  pay_mismatch: PayMismatch | null;
  /** Когда штука заказа последний раз встала в «Получено/Упаковано»; `null` — не было. */
  received_at: string | null;
  /** Штук принято за период отбора «дата приёма»; `null` — отбора нет. */
  received_in_window: number | null;
} & StageMark;

export type OrderTotals = GoodsCounts & {
  orders: number;
  goods: number;
  accepted: number;
  waiting: number;
  positions: number;
  sum_goods: number;
  sum_delivery: number;
  /** Платежи посчитаны DISTINCT: подвал не завышен. */
  paid: number;
  payments: number;
};

export type OrdersAnswer = {
  /** Статусы штук с группой — колонки таблицы (разметка 20.10.26 п.4). */
  unit_statuses: UnitStatusColumn[];
  rows: OrderRow[];
  totals: OrderTotals;
  /** Список плитки воронки: к открытым заказам окно дат не применялось. */
  by_stage: boolean;
  /** Отбор `?crm=` — показатель индексера CRM; `null` — без него. */
  crm_indicator: { code: string; label: string } | null;
  limited: boolean;
  limit: number;
  window: { since: string; until: string };
  /** Отбор «дата приёма» (`rsince/runtil`): окно даты заказа к нему не применялось. */
  received_window: { since: string; until: string } | null;
  /** Применённые сервером фильтры колонок `vf`. */
  value_filters: { key: string; from: number | null; to: number | null }[];
  /** Заказов отбора до фильтра колонок. */
  rows_before_filter: number;
};

export type OrdersFilters = {
  suppliers: { id: number; name: string; full: string | null; order_count: number }[];
  categories: { id: number; name: string; order_count: number }[];
  statuses: { id: number; name: string; order_count: number }[];
  /** Города назначения треков по России. */
  cities: { id: number; name: string; order_count: number }[];
  /** Смена статуса заказа (разметка 20.10.26 п.3): право CRM сотрудника и
   *  причина, по которой запись закрыта (застава, гранты); `null` — открыта. */
  status_write: OrderStatusWrite;
};

export type OrderStatusWrite = { can: boolean; blocked: string | null };

/** Сменить статус заказа — запись кнопок «Принять / Закрыть / Брак» CRM. */
export function changeOrderStatus(
  orderId: number,
  fromStatusId: number,
  toStatusId: number,
): Promise<{ order_id: number; from_status_id: number; to_status_id: number; changed_fields: string[] }> {
  return apiFetch(`/api/orders/${orderId}/status`, {
    method: "POST",
    body: JSON.stringify({ from_status_id: fromStatusId, to_status_id: toStatusId }),
  });
}

/**
 * Пометка под суммой оплат.
 *
 * Платёж часто покрывает несколько заказов (185 из 835 на `crm-dev`), и в
 * строке он показан ЦЕЛИКОМ. Без этой пометки строка врала бы, что заказ на
 * 39 375 ¥ оплачен на 89 000 ¥.
 */
export function paymentNote(
  row: Pick<OrderRow, "paid" | "payments" | "payment_orders_max">,
): string {
  if (!row.paid || row.payment_orders_max <= 1) return "";
  const n = row.payment_orders_max;
  return `платёж на ${n} ${plural(n, ["заказ", "заказа", "заказов"])}`;
}

/** Колонка «план»: сколько партий плана закрывает заказ и сколько в них штук. */
export function planLabel(row: Pick<OrderRow, "plan_dates" | "plan_qty">): string {
  return row.plan_dates ? `${row.plan_dates} парт. · ${num(row.plan_qty)} шт` : "—";
}

/** Номера для смарт-кнопок CRM: без повторов, в порядке строк — как `joinUniq`. */
export type SmartOrder = Pick<OrderRow, "order_id" | "qr_ids" | "plan_date_ids" | "plan_ids"> & {
  cargo: { media_file_id: number }[];
};

export function orderSmartIds(rows: SmartOrder[]): SmartIds {
  const out = {
    supplyOrderID: [] as number[],
    barcodeQrDataID: [] as number[],
    mpStockSupplyPlanDateID: [] as number[],
    mpStockSupplyPlanID: [] as number[],
    mediaFileID: [] as number[],
  };
  const положить = (список: number[], id: number) => {
    if (id && !список.includes(id)) список.push(id);
  };
  for (const r of rows) {
    положить(out.supplyOrderID, r.order_id);
    r.qr_ids.forEach((id) => положить(out.barcodeQrDataID, id));
    r.plan_date_ids.forEach((id) => положить(out.mpStockSupplyPlanDateID, id));
    r.plan_ids.forEach((id) => положить(out.mpStockSupplyPlanID, id));
    r.cargo.forEach((c) => положить(out.mediaFileID, c.media_file_id));
  }
  return out;
}

export function loadOrders(params: URLSearchParams): Promise<OrdersAnswer> {
  return apiFetch<OrdersAnswer>(`/api/orders?${params.toString()}`);
}

/**
 * «Сводка по товарам» заказов списка (спека
 * `docs/specs/2026-10-08-orders-goods-summary-design.md`). Группы — в порядке
 * экрана; `no_product` — позиции без товара площадки, номеров у неё нет.
 */
export type OrdersGoodsKey =
  | "all"
  | "wb_stock"
  | "wb_empty"
  | "ozon_card"
  | "ozon_stock"
  | "ozon_empty"
  | "ozon_none"
  | "no_product";

export type OrdersGoodsGroup = {
  key: OrdersGoodsKey;
  label: string;
  products: number;
  models: number;
  pieces: number;
  waiting: number;
  product_ids: number[];
};

export type OrdersGoodsAnswer = {
  orders: number;
  /** заказов категории «Образцы», выкинутых из сводки */
  samples: number;
  by_stage: boolean;
  limited: boolean;
  limit: number;
  window: { since: string; until: string };
  crm_indicator: { code: string; label: string } | null;
  groups: OrdersGoodsGroup[];
};

export function loadOrdersGoods(params: URLSearchParams): Promise<OrdersGoodsAnswer> {
  return apiFetch<OrdersGoodsAnswer>(`/api/orders/goods?${params.toString()}`);
}

export function loadOrdersFilters(): Promise<OrdersFilters> {
  return apiFetch<OrdersFilters>("/api/orders/filters");
}
